import { useEffect, useRef } from "react";

const isAIAgent = (ticket) => {
  if (!ticket) return false;
  return (
    ticket.promptId != null ||
    ticket.chatbot === true ||
    ticket.integrationId != null ||
    ticket.flowStatus != null
  );
};

/**
 * Gestiona el ciclo completo de suscripción Socket.IO para TicketsListCustom.
 * Emite joinTickets en mount/reconnect y leaveTickets en cleanup.
 * Hexagonal: este hook es el único punto de contacto con el socket para este componente.
 */
const useTicketsListCustomSocket = ({
  status,
  showAll,
  user,
  selectedQueueIds,
  profile,
  queues,
  socketManager,
  excludeAIAgents,
  dispatch,
  isMounted,
  ticketsListRef,
}) => {
  const presenceTimeouts = useRef({});

  useEffect(() => {
    isMounted.current = true;

    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const shouldUpdateTicket = (ticket) => {
      const ticketUserIdNum = ticket.userId ? Number(ticket.userId) : null;
      const currentUserIdNum = user?.id ? Number(user?.id) : null;
      const userQueueIds = (queues || []).map(q => Number(q.id));
      const ticketInUserDept = !ticket.queueId || userQueueIds.includes(Number(ticket.queueId));
      const hasPermission = showAll || ticketUserIdNum === currentUserIdNum || !ticket.userId || (profile !== "admin" && ticketInUserDept && ticket.status !== "open");
      if (!hasPermission) return false;

      const isGroupTicket = ticket.contact && ticket.contact.isGroup;
      if (status === "ai") {
        const ticketHasAI = ticket.promptId || ticket.useIntegration || ticket.chatbot || ticket.flowStatus != null;
        if (!ticketHasAI) return false;
      } else if (status === "all") {
        if ((ticket.status !== "open" && ticket.status !== "pending") || isGroupTicket) return false;
      } else if (status === "group") {
        if (!isGroupTicket || ticket.status === "closed") return false;
      } else if (status === "closed") {
        if (ticket.status !== "closed") return false;
      } else if (ticket.status !== status || isGroupTicket) {
        return false;
      }

      const showTicketWithoutQueue = user?.allTicket === "enabled";
      if (!showTicketWithoutQueue && selectedQueueIds && selectedQueueIds.length > 0 && ticket.queueId) {
        if (!selectedQueueIds.includes(ticket.queueId)) return false;
      }
      return true;
    };

    // ── Suscripción ──────────────────────────────────────────────────────────
    const subscribe = () => {
      if (status) socket.emit("joinTickets", status);
      else socket.emit("joinNotification");
    };

    subscribe();
    socket.on("ready", subscribe);
    socket.on("connect", subscribe);

    // ── Tickets ──────────────────────────────────────────────────────────────
    const handleTicket = (data) => {
      try {
        if (!isMounted.current) return;
        if (!data || typeof data !== "object") return;

        if (data.action === "update" && data.ticket?.lastMessageWithReactions) {
          dispatch({
            type: "UPDATE_TICKET_REACTIONS",
            payload: { ticketId: data.ticket.id, lastMessageWithReactions: data.ticket.lastMessageWithReactions },
          });
          return;
        }

        if (data.action === "create") {
          const ticketHasAI = isAIAgent(data.ticket);
          const ticketStatus = data.ticket.status;
          const isGroupTicket = data.ticket.contact && data.ticket.contact.isGroup;

          let shouldCreate = false;
          if (status === "ai" && ticketHasAI) {
            shouldCreate = true;
          } else if (status === "group" && isGroupTicket && (ticketStatus === "open" || ticketStatus === "pending")) {
            shouldCreate = true;
          } else if (status === "closed" && ticketStatus === "closed") {
            shouldCreate = true;
          } else if (status !== "ai" && status !== "group" && status !== "closed" && !ticketHasAI && !isGroupTicket && ticketStatus === status) {
            shouldCreate = true;
          }

          const ticketUserIdNum = data.ticket.userId ? Number(data.ticket.userId) : null;
          const currentUserIdNum = user?.id ? Number(user?.id) : null;
          const userQueueIds = (queues || []).map(q => Number(q.id));
          const ticketInUserDept = !data.ticket.queueId || userQueueIds.includes(Number(data.ticket.queueId));
          const hasUserPermission = showAll || ticketUserIdNum === currentUserIdNum || !data.ticket.userId || (profile !== "admin" && ticketInUserDept && data.ticket.status !== "open");

          if (shouldCreate && hasUserPermission && !(ticketsListRef.current || []).find(t => t.id === data.ticket.id)) {
            dispatch({ type: "ADD_TICKET", payload: data.ticket });
          }
        }

        if (data.action === "updateUnread") {
          const count = typeof data.unreadMessages === "number" ? data.unreadMessages : 0;
          if (count === 0) {
            dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
          }
        }

        if (data.action === "update") {
          const ticketHasAI = isAIAgent(data.ticket);
          const ticketStatus = data.ticket.status;
          const isGroupTicket = data.ticket.contact && data.ticket.contact.isGroup;
          const existingTicket = (ticketsListRef.current || []).find(t => t.id === data.ticket.id);

          let shouldBeInThisTab = false;
          if (status === "ai") {
            shouldBeInThisTab = ticketHasAI;
          } else if (status === "group") {
            shouldBeInThisTab = isGroupTicket && (ticketStatus === "open" || ticketStatus === "pending");
          } else if (status === "closed") {
            shouldBeInThisTab = ticketStatus === "closed";
          } else if (status !== "ai" && status !== "group" && status !== "closed") {
            shouldBeInThisTab = !ticketHasAI && !isGroupTicket && ticketStatus === status;
          }

          const ticketUserIdNum = data.ticket.userId ? Number(data.ticket.userId) : null;
          const currentUserIdNum = user?.id ? Number(user?.id) : null;
          const userQueueIds = (queues || []).map(q => Number(q.id));
          const ticketInUserDept = !data.ticket.queueId || userQueueIds.includes(Number(data.ticket.queueId));
          const hasUserPermission = showAll || ticketUserIdNum === currentUserIdNum || !data.ticket.userId || (profile !== "admin" && ticketInUserDept && data.ticket.status !== "open");

          if (shouldBeInThisTab && hasUserPermission) {
            dispatch({ type: existingTicket ? "UPDATE_TICKET" : "ADD_TICKET", payload: data.ticket });
          } else if (existingTicket) {
            dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
          }
        }

        if (data.action === "delete") {
          // ticketsListRef puede estar un render detrás del estado real (useEffect asíncrono).
          // El reducer DELETE_TICKET es idempotente — no-op si el ticket no existe.
          dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
        }
      } catch (err) {
        console.error("❌ useTicketsListCustomSocket: ticket:", err);
      }
    };
    socket.on(`company-${companyId}-ticket`, handleTicket);

    // ── AppMessage ───────────────────────────────────────────────────────────
    const handleAppMessage = (data) => {
      try {
        if (!isMounted.current) return;
        if (!data || typeof data !== "object") return;

        const queueIds = queues.map(q => q.id);
        if (profile === "user" && (queueIds.indexOf(data.ticket?.queue?.id) === -1 || data.ticket?.queue === null)) return;

        const isGroupTicket = data.ticket?.contact?.isGroup;
        const isMessageFromMe = data.message?.fromMe === true;
        const ticketExists = (ticketsListRef.current || []).some(t => t.id === data.ticket?.id);

        if (data.action === "create" && data.ticket) {
          if (ticketExists || isMessageFromMe) {
            dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
          } else if (shouldUpdateTicket(data.ticket) && (status === undefined || data.ticket.status === status) && !isGroupTicket) {
            dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
          }
        }

        if (data.action === "update" && data.ticket) {
          dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
        }
      } catch (err) {
        console.error("❌ useTicketsListCustomSocket: appMessage:", err);
      }
    };
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);

    // ── Presence ─────────────────────────────────────────────────────────────
    const handlePresence = (data) => {
      try {
        if (!isMounted.current) return;
        if (!data || typeof data !== "object") return;

        if (data.presence === "composing") {
          const { ticketId } = data;
          const existing = presenceTimeouts.current[ticketId];
          if (existing) clearTimeout(existing);

          presenceTimeouts.current[ticketId] = setTimeout(() => {
            dispatch({ type: "UPDATE_TICKET_PRESENCE", payload: { ticketId, presence: "available" } });
            delete presenceTimeouts.current[ticketId];
          }, 10000);
        }

        dispatch({ type: "UPDATE_TICKET_PRESENCE", payload: data });
      } catch (err) {
        console.error("❌ useTicketsListCustomSocket: presence:", err);
      }
    };
    socket.on(`company-${companyId}-presence`, handlePresence);

    // ── Contact ──────────────────────────────────────────────────────────────
    const handleContact = (data) => {
      try {
        if (!isMounted.current) return;
        if (!data || typeof data !== "object") return;
        if (data.action === "update") {
          dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
        }
      } catch (err) {
        console.error("❌ useTicketsListCustomSocket: contact:", err);
      }
    };
    socket.on(`company-${companyId}-contact`, handleContact);

    // ── Cleanup ──────────────────────────────────────────────────────────────
    return () => {
      isMounted.current = false;

      if (status) socket.emit("leaveTickets", status);
      else socket.emit("leaveNotification");

      socket.off("ready", subscribe);
      socket.off("connect", subscribe);
      socket.off(`company-${companyId}-ticket`, handleTicket);
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      socket.off(`company-${companyId}-presence`, handlePresence);
      socket.off(`company-${companyId}-contact`, handleContact);

      Object.values(presenceTimeouts.current).forEach(clearTimeout);
      presenceTimeouts.current = {};
    };
  }, [status, showAll, user?.id, selectedQueueIds, profile, queues, socketManager, excludeAIAgents]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useTicketsListCustomSocket;
